export type ImageLoader = (src: string, width?: number) => string;

export interface ImageProvider {
  src(url: string, width?: number): string;
  srcSet(url: string, width?: number): string;
}

// Only use a fixed number of sizes, to improve the CDN cache hit rate.
const SIZES = [256, 480, 768, 960, 1120, 3840];

export class FixedSizeProvider implements ImageProvider {
  private readonly loader: ImageLoader;

  constructor(loader: ImageLoader) {
    this.loader = loader;
  }

  src(src: string, width?: number): string {
    return this.loader(
      src,
      SIZES.find((x) => x >= (width || Infinity)) || SIZES[SIZES.length - 1],
    );
  }

  srcSet(src: string, width?: number): string {
    const maxWidth = width ?? Infinity;
    const cutoff = SIZES.findIndex((size) => size >= maxWidth);
    const widths = cutoff === -1 ? SIZES : SIZES.slice(0, cutoff + 1);
    return widths
      .map((size) => `${this.loader(src, size)} ${size}w`)
      .join(', ');
  }
}

const contentstack = new FixedSizeProvider(
  (src: string, width?: number): string => {
    const url = new URL(src);
    if (width) {
      url.searchParams.set('width', width.toString(10));
      url.searchParams.set('disable', 'upscale');
      url.searchParams.set('fit', 'bounds');
    }
    url.searchParams.set('auto', 'webp');
    return url.toString();
  },
);

export function findImageProvider(
  src: string | undefined | null,
): ImageProvider | null {
  if (src == null) return null;
  try {
    return new URL(src).hostname === 'images.contentstack.io'
      ? contentstack
      : null;
  } catch (e) {
    return null;
  }
}
